import React, { CSSProperties, useCallback, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  IconButton,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { userDataSelectors } from '../../../redux/userData';
import {
  MemberInfoCell,
  priceFormat,
} from '../../../components/tableCellRenders';
import {
  getAccountDummyProjects,
  getSelectedProject,
  getVisibleAccountDummyMembers,
  selectHiddenAccountMembers,
  setGroupBy,
  GroupAccountMembersBy,
  toggleEditableMode,
  setEditableForMembers,
  setSelectedProjectId,
  getDateFilterValueLocal,
  currentYearRevenuePrompt,
} from '../../../redux/account';
import {
  AccountDetail,
  AccountStatus,
  DummyMember,
  ProjectDetail,
  ProjectedRevenuePayCostLog,
} from '../../../types';
import { avatarMediumSize } from '../../../theme';
import { Info } from '../../../features/budgets/BudgetsHeader/Info/Info';
import { CustomNumber } from '../../../features/budgets/BudgetsHeader/Number/Number';
import { MarginSummary } from '../../../features/budgets/BudgetsHeader/MarginSummary/MarginSummary';
import {
  formatDateTo_MMM_dd_yyyy,
  getCurrentYear,
  getRevenueLastInvoiceDateFormated,
} from '../../../utils';
import {
  IconName,
  CustomIcon,
} from '../../../components/customIcon/CustomIcon';
import { COLORS } from '../../../utils/colors';
import EditHeadcountChangeModal from '../../../features/editHeadcountChangesModal/EditHeadcountChangesModal';
import EditProjectedRevenueEndDateModal from '../../../features/editProjectedRevenueEndDate/EditProjectedRevenueEndDate';
import EditOperationalCostModal from '../../../features/editOperationalCost/EditOperationalCost';
import { DateTime } from 'luxon';
import _ from 'lodash';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import ReceiptRoundedIcon from '@mui/icons-material/ReceiptRounded';
import { useNavigate } from 'react-router-dom';
import { paths } from '../../../constants/paths';

import { CsvDownloadButton } from '../../../features/budgets/DownloadCsv/DownloadCsv';
import FinanceStats from '../../../features/financeStats/FinanceStats';
import {
  getProjectedRevenueLogsAmount,
  getTypePrompt,
} from '../../../utils/business';
import { selectinvoicesData } from '../../../redux/invoices';

interface Props {
  account: AccountDetail;
  projects: ProjectDetail[] | null;
  handleOnOpenRaiseBudgetModal?: () => void;
}

export const AccountDetailsSummary = ({ account, projects }: Props) => {
  const visibleAccountDummyMembers: DummyMember[] | undefined = useAppSelector(
    getVisibleAccountDummyMembers,
  );

  const dispatch = useAppDispatch();

  const hiddenAccountMembers = useAppSelector(selectHiddenAccountMembers);
  const invoicesPageData = useAppSelector(selectinvoicesData);
  const dummyProjects = useAppSelector(getAccountDummyProjects);
  const project = useAppSelector(getSelectedProject);
  const dateFilterValueLocal = useAppSelector(getDateFilterValueLocal);

  const budget = project ? project : account;

  const { totalDebt, totalPaid, totalInvoiced } = useMemo(() => {
    const total = invoicesPageData.invoices.data?.reduce(
      (acc, invoice) => {
        return {
          totalDebt: acc.totalDebt + (invoice.debt ?? 0),
          totalPaid: acc.totalPaid + (invoice.amountPaid ?? 0),
          totalInvoiced: acc.totalInvoiced + (invoice.amount ?? 0),
        };
      },
      { totalDebt: 0, totalPaid: 0, totalInvoiced: 0 },
    );

    return {
      totalDebt: total?.totalDebt ? Math.round(total?.totalDebt) : null,
      totalPaid: total?.totalPaid ? Math.round(total?.totalPaid) : null,
      totalInvoiced: Math.round(
        total?.totalInvoiced ?? budget.pastRevenue ?? 0,
      ),
    };
  }, [invoicesPageData, budget.pastRevenue]);

  const user = useAppSelector(userDataSelectors.getUserData);
  const hasAccounts = Boolean(user?.permissions?.hasAccounts);

  const alertMailAnchor = React.useRef<HTMLElement | null>(null);
  const buttonMailAnchor = React.useRef<HTMLElement | null>(null);
  const forcastedRevenuePopoverAnchor = React.useRef<HTMLElement | null>(null);
  const netMarginPopoverAnchor = React.useRef<HTMLElement | null>(null);
  const [openMailPopover, setOpenMailPopover] = React.useState<boolean>(false);

  const [openAddButtonAlert, setOpenAddButtonAlert] =
    React.useState<boolean>(false);
  const [openForcastedRevenuePopover, setOpenForcastedRevenuePopover] =
    React.useState<boolean>(false);
  const [isHeadcountModalOpen, setIsHeadcountModalOpen] = useState(false);
  const [
    isProjectedRevenueEndDateModalOpen,
    setIsProjectedRevenueEndDateModalOpen,
  ] = useState(false);
  const [isOperationalCostModalOpen, setIsOperationalCostModalOpen] =
    useState(false);

  const [openUsersOnProjectStatsModal, setOpenUsersOnProjectStatsModal] =
    useState(false);

  const navigate = useNavigate();

  const handleToggleHeadcountModal = useCallback(() => {
    setIsHeadcountModalOpen(!isHeadcountModalOpen);
  }, [isHeadcountModalOpen, setIsHeadcountModalOpen]);

  const handleToggleProjectedRevenueEndDateModal = useCallback(() => {
    setIsProjectedRevenueEndDateModalOpen(!isProjectedRevenueEndDateModalOpen);
  }, [
    isProjectedRevenueEndDateModalOpen,
    setIsProjectedRevenueEndDateModalOpen,
  ]);

  const handleToggleOperationalCostModal = useCallback(() => {
    setIsOperationalCostModalOpen((prev) => !prev);
  }, []);

  const handleAlertMailMouseOver = useCallback(() => {
    setOpenMailPopover(true);
  }, []);

  const handleAlertMailClose = useCallback(() => {
    setOpenMailPopover(false);
  }, []);

  const handleButtonMouseOver = useCallback(() => {
    setOpenAddButtonAlert(true);
  }, []);

  const handleButtonMouseClose = useCallback(() => {
    setOpenAddButtonAlert(false);
  }, []);

  const handleForcastedRevenuePopoverOpen = useCallback(() => {
    setOpenForcastedRevenuePopover(true);
  }, []);

  const handleForcastedRevenuePopoverClose = useCallback(() => {
    setOpenForcastedRevenuePopover(false);
  }, []);

  const headcountSummary = useMemo(() => {
    if (project) return project.headcountSummary;
    return account?.headcountSummary;
  }, [account, project]);

  const { membersInTeams } = useMemo(() => {
    return (
      projects?.reduce(
        (
          { membersInTeams }: { membersInTeams: number },
          project: ProjectDetail,
        ) => ({
          membersInTeams: membersInTeams + project.membersCount,
        }),
        { membersInTeams: 0 },
      ) ?? { membersInTeams: 0 }
    );
  }, [projects]);

  const updatedMembers = useMemo(() => {
    return visibleAccountDummyMembers?.length
      ? membersInTeams + visibleAccountDummyMembers.length
      : membersInTeams;
  }, [visibleAccountDummyMembers, membersInTeams]);

  const operationalCostForSelectedPeriod = useMemo(() => {
    const currentPeriod = dateFilterValueLocal
      ? Number(DateTime.fromISO(dateFilterValueLocal).toFormat('yyyyMM'))
      : Number(DateTime.now().toFormat('yyyyMM'));
    const operationalCostData =
      account.operationalCost &&
      [...account.operationalCost]
        .sort((a, b) => b.period - a.period)
        ?.find((data) => data.period <= currentPeriod);
    return (
      operationalCostData || {
        period: currentPeriod,
        operationalCost: 11,
      }
    );
  }, [account, dateFilterValueLocal]);

  const revenueLastInvoiceDateFormated = getRevenueLastInvoiceDateFormated(
    budget.revenueLastInvoiceDate ?? null,
  );

  const calculateMargins = useCallback(() => {
    dispatch(toggleEditableMode());
    dispatch(setGroupBy(GroupAccountMembersBy.Team));
    dispatch(setEditableForMembers());
  }, [dispatch]);

  const projectedHeadcountMessage = useMemo(() => {
    if (!headcountSummary) return '';
    const joiningCount = Number(
      headcountSummary.projectedJoiningHeadcount || 0,
    );
    const leavingCount = Number(
      headcountSummary.projectedLeavingHeadcount || 0,
    );
    const formattedDate = formatDateTo_MMM_dd_yyyy(
      new Date(headcountSummary?.projectedHeadcountStartDate),
    );

    const joinText = joiningCount
      ? `${joiningCount} ${
          joiningCount > 1 ? 'resources' : 'resource'
        } will join`
      : '';
    const leaveText = leavingCount
      ? `${Math.abs(leavingCount)} ${
          Math.abs(leavingCount) > 1 ? 'resources' : 'resource'
        } will leave`
      : '';
    const separator = joiningCount && leavingCount ? ' and ' : '';

    return `${joinText}${separator}${leaveText} on ${formattedDate}`;
  }, [headcountSummary]);

  const redirectToInvoicesPage = useCallback(() => {
    dispatch(setSelectedProjectId({ projectId: null }));
    navigate(`${paths.portfolio}/${account.id}/${paths.invoices}`);
  }, [navigate, account.id, dispatch]);

  if (!hasAccounts) {
    return null;
  }

  return (
    <Card
      sx={{
        backgroundColor: '#fff',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
        padding: '16px 8px 16px 8px',
        borderRadius: (theme) => theme.spacing(1, 1, 0, 0),
        display: 'grid',
        gap: '16px',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '2fr 3fr 3fr min-content',
          alignItems: 'stretch',
          paddingBottom: '8px',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Box>
          <Info title="Teams">
            <CustomNumber
              typographyStyle={{
                fontWeight: 600,
                fontSize: '24px',
                lineHeight: '100%',
              }}
              superscript={dummyProjects?.length ?? 0}
            >
              {(projects?.length ?? 0) + (dummyProjects?.length ?? 0)}
            </CustomNumber>
          </Info>
        </Box>
        <Box>
          <Info title="Headcount">
            <CustomNumber
              superscript={visibleAccountDummyMembers?.length ?? undefined}
              subscript={hiddenAccountMembers?.length ?? undefined}
              typographyStyle={{
                fontWeight: 600,
                fontSize: '24px',
                lineHeight: '100%',
              }}
            >
              {updatedMembers - hiddenAccountMembers?.length}
            </CustomNumber>
          </Info>
        </Box>

        <Box>
          <Info title="Projected">
            <Box ref={alertMailAnchor}>
              {headcountSummary &&
              (headcountSummary.projectedJoiningHeadcount > 0 ||
                headcountSummary.projectedLeavingHeadcount < 0) ? (
                <>
                  <Box
                    onMouseEnter={handleAlertMailMouseOver}
                    onMouseLeave={handleAlertMailClose}
                  >
                    <CustomNumber
                      typographyStyle={{
                        fontWeight: 600,
                        fontSize: '24px',
                        lineHeight: '100%',
                        color: '#666666',
                      }}
                    >
                      {headcountSummary.projectedHeadcount > 0 && '+'}
                      {headcountSummary.projectedHeadcount}
                    </CustomNumber>
                    <MyPortfolioHeaderPopover
                      anchorEl={alertMailAnchor}
                      handleClose={handleAlertMailClose}
                      handleMouseOver={handleAlertMailMouseOver}
                      isOpen={openMailPopover}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          alignContent: 'center',
                          color: 'text.secondary',
                          justifyContent: 'space-between',
                          gap: 1,
                          marginBottom: 1,
                        }}
                      >
                        <Typography>
                          {headcountSummary && projectedHeadcountMessage}
                        </Typography>
                      </Box>
                    </MyPortfolioHeaderPopover>
                  </Box>
                </>
              ) : (
                <Box display="flex" flexDirection="row" ref={buttonMailAnchor}>
                  <Box
                    display="flex"
                    flexDirection="row"
                    ref={buttonMailAnchor}
                  >
                    <Button
                      variant="contained"
                      onClick={handleToggleHeadcountModal}
                      size="small"
                      sx={{
                        backgroundColor: COLORS.HIGHLIGHTS.DANGER,
                        '&:hover': {
                          backgroundColor: COLORS.STATUS.CRITICAL,
                        },
                        height: '27px',
                      }}
                      onMouseOver={handleButtonMouseOver}
                      onMouseLeave={handleButtonMouseClose}
                      disabled={account.status === AccountStatus.Closed}
                    >
                      <CustomIcon
                        icon={IconName.Edit}
                        sx={{
                          color:
                            account.status === AccountStatus.Closed
                              ? ''
                              : '#fff',
                          fontSize: '18px',
                        }}
                      />
                      <Typography variant="body1" sx={{ marginLeft: 0.7 }}>
                        Add
                      </Typography>
                    </Button>
                  </Box>
                  <MyPortfolioHeaderPopover
                    anchorEl={buttonMailAnchor}
                    handleClose={handleButtonMouseClose}
                    handleMouseOver={handleAlertMailMouseOver}
                    isOpen={openAddButtonAlert}
                  >
                    <>
                      <Typography>
                        {`No projected headcounts available for this 
                        ${project ? 'project' : 'account'}.`}
                      </Typography>
                      <Typography>Please add a projected headcount.</Typography>
                    </>
                  </MyPortfolioHeaderPopover>
                </Box>
              )}
            </Box>
          </Info>
        </Box>
        <Box>
          {headcountSummary &&
            (headcountSummary.projectedJoiningHeadcount > 0 ||
              headcountSummary.projectedLeavingHeadcount < 0) && (
              <IconButton onClick={handleToggleHeadcountModal} size="small">
                <CustomIcon icon={IconName.Edit} sx={{ color: '#3F8CFF' }} />
              </IconButton>
            )}
        </Box>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          paddingBottom: '8px',
          borderBottom: '1px solid #e0e0e0',
        }}
        ref={netMarginPopoverAnchor}
      >
        <MarginSummary
          marginHighlights={budget.grossMarginHighlights}
          grossMargin={budget?.grossMargin ?? undefined}
          netMarginHighlights={budget.netMarginHighlights}
          netMargin={budget?.netMargin ?? undefined}
        />
      </Box>

      <SummaryStat
        title={`${getCurrentYear(account.currentYear)} invoiced`}
        value={budget.pastRevenueCurrentYear}
        tooltip={currentYearRevenuePrompt}
      />

      <SummaryStat
        title="Lifetime invoiced"
        value={totalInvoiced}
        tooltip={revenueLastInvoiceDateFormated}
      />

      {totalDebt && (
        <SummaryStat
          title="Debt"
          value={totalDebt}
          tooltip="How much debt is accumulated across ALL invoices for this account"
        />
      )}

      {totalPaid && (
        <SummaryStat
          title="Paid"
          value={totalPaid}
          tooltip="How much is invoiced for this account"
        />
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          paddingBottom: '8px',
          borderBottom: '1px solid #e0e0e0',
        }}
        ref={forcastedRevenuePopoverAnchor}
      >
        <Info title="Forecasted revenue">
          <CustomNumber
            typographyStyle={{
              fontWeight: 600,
              fontSize: '24px',
              lineHeight: '100%',
            }}
          >
            {priceFormat({
              value: budget.projectedRevenue
                ? Math.round(budget.projectedRevenue)
                : budget.projectedRevenue,
              hideCents: true,
            })}
          </CustomNumber>
        </Info>

        <InfoOutlinedIcon
          color="secondary"
          sx={{
            fontSize: '1.5rem',
            alignSelf: 'center',
            width: '2.5rem',
          }}
          onMouseEnter={handleForcastedRevenuePopoverOpen}
          onMouseLeave={handleForcastedRevenuePopoverClose}
        />
        <MyPortfolioHeaderPopover
          anchorEl={forcastedRevenuePopoverAnchor}
          handleClose={handleForcastedRevenuePopoverClose}
          handleMouseOver={handleForcastedRevenuePopoverOpen}
          isOpen={openForcastedRevenuePopover}
        >
          <ProjectRevenueDisplay
            project={project}
            account={account}
            redirectToInvoicesPage={redirectToInvoicesPage}
          />
        </MyPortfolioHeaderPopover>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto',
          gap: '8px',
          paddingBottom: '8px',
          borderBottom: '1px solid #e0e0e0',
          alignItems: 'center',
        }}
      >
        <Info title="Operational cost">
          <CustomNumber
            typographyStyle={{
              fontWeight: 600,
              fontSize: '24px',
              lineHeight: '100%',
            }}
          >
            {priceFormat({
              value: operationalCostForSelectedPeriod.operationalCost,
              hideCents: true,
              showPeriod: true,
            })}
          </CustomNumber>
        </Info>
        {(user?.permissions.isFinanceAdmin ||
          user?.permissions.isFinanceManager) && (
          <Tooltip title="Edit operational cost" placement="top">
            <IconButton onClick={handleToggleOperationalCostModal}>
              <CustomIcon
                icon={IconName.Edit}
                sx={{ color: '#3F8CFF', fontSize: '20px' }}
              />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <EditOperationalCostModal
        handleCloseModal={handleToggleOperationalCostModal}
        isModalOpen={isOperationalCostModalOpen}
        operationalCostList={account.operationalCost}
      />

      {project && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: '8px',
            paddingBottom: '8px',
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <Info title="End date forecast">
            <Typography variant="h4" padding={'0px 5px'}>
              {project.projectedRevenueEndDate
                ? DateTime.fromFormat(
                    project.projectedRevenueEndDate,
                    'yyyy-LL-dd',
                  ).toFormat('LLLL dd, yyyy')
                : 'N/A'}
            </Typography>
          </Info>

          <Tooltip title="Edit forecasted end date" placement="top">
            <IconButton onClick={handleToggleProjectedRevenueEndDateModal}>
              <CustomIcon icon={IconName.Edit} sx={{ color: '#3F8CFF' }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '2fr auto',
          gap: '8px',
          paddingBottom: '8px',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Stack direction="column">
          <Box my={1}>
            {account.portfolioDeliveryManager ? (
              <MemberInfoCell
                userInfo={{
                  ...account.portfolioDeliveryManager,
                  stream: account.portfolioDeliveryManager.designation,
                }}
                roleLabel="PDM"
                key={account.portfolioDeliveryManager.id}
              />
            ) : (
              <NotAssignedMemberCell />
            )}
          </Box>
          <Box my={1}>
            {account.deliveryManager ? (
              <MemberInfoCell
                userInfo={{
                  ...account.deliveryManager,
                  stream: account.deliveryManager.designation,
                }}
                roleLabel="DM"
                key={account.deliveryManager.id}
              />
            ) : (
              <NotAssignedMemberCell />
            )}
          </Box>
        </Stack>
      </Box>

      <Stack
        direction="row"
        alignItems="end"
        gap={1}
        sx={{ marginBottom: 2, width: '100%' }}
      >
        <Button
          variant="contained"
          size="small"
          onClick={calculateMargins}
          disabled={account.status === AccountStatus.Closed}
          sx={{ width: '65%' }}
        >
          <CustomIcon
            icon={IconName.Edit}
            sx={{
              color: account.status === AccountStatus.Closed ? '' : '#fff',
              fontSize: '18px',
            }}
          />
          <Typography variant="body1" sx={{ marginLeft: 0.7 }}>
            Calculate margins
          </Typography>
        </Button>
        <CsvDownloadButton />
      </Stack>

      {/* <Info title="Users on Project">
        <Button
          variant="outlined"
          size="small"
          onClick={() => setOpenUsersOnProjectStatsModal(true)}
          sx={{ minWidth: '120px' }}
        >
          View Stats
        </Button>
      </Info> */}

      <FinanceStats
        account={account}
        open={openUsersOnProjectStatsModal}
        onClose={() => setOpenUsersOnProjectStatsModal(false)}
      />

      <EditHeadcountChangeModal
        handleCloseModal={handleToggleHeadcountModal}
        isModalOpen={isHeadcountModalOpen}
      />

      <EditProjectedRevenueEndDateModal
        handleCloseModal={handleToggleProjectedRevenueEndDateModal}
        isModalOpen={isProjectedRevenueEndDateModalOpen}
      />
    </Card>
  );
};

// One money figure in the summary sidebar: title, amount, and an info tooltip
const SummaryStat = ({
  title,
  value,
  tooltip,
}: {
  title: string;
  value?: number | null;
  tooltip: string;
}) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      paddingBottom: '8px',
      borderBottom: '1px solid #e0e0e0',
    }}
  >
    <Info title={title}>
      <CustomNumber
        typographyStyle={{
          fontWeight: 600,
          fontSize: '24px',
          lineHeight: '100%',
        }}
        info
      >
        {priceFormat({ value, hideCents: true })}
      </CustomNumber>
    </Info>

    <Tooltip
      sx={{
        textAlign: 'center',
        justifyContent: 'center',
        display: 'flex',
      }}
      title={tooltip}
      placement="top"
    >
      <Box>
        <CustomIcon
          icon={IconName.Info}
          sx={{
            color: '#666666',
            alignSelf: 'flex-end',
            margin: '8px',
          }}
        />
      </Box>
    </Tooltip>
  </Box>
);

const NotAssignedMemberCell: React.FC = () => {
  return (
    <Stack direction="row" alignItems="center">
      <Avatar
        src={''}
        sx={{
          width: avatarMediumSize,
          height: avatarMediumSize,
          bgcolor: 'gray',
        }}
      />
      <Typography variant="h4" sx={{ marginLeft: 1, opacity: 0.7 }}>
        Not Assigned
      </Typography>
    </Stack>
  );
};

const ProjectRevenueDisplay = ({
  project,
  account,
  redirectToInvoicesPage,
}: {
  project?: any;
  account: any;
  redirectToInvoicesPage: () => void;
}) => {
  if (project) {
    return (
      <Box>
        {project.projectedRevenueLogs?.map(
          (log: ProjectedRevenuePayCostLog, index: number) => (
            <TableRow
              key={index}
              sx={{
                textAlign: 'left',
              }}
            >
              <TableCell>
                <Typography variant="body2">
                  {getTypePrompt(log, index, project.projectedRevenueLogs)}
                </Typography>
                <Typography variant="h2" marginBlock={1}>
                  {priceFormat({
                    value: getProjectedRevenueLogsAmount(
                      index,
                      project.projectedRevenueLogs,
                      'blendedBillRate',
                    ),
                    hideCents: true,
                  })}
                </Typography>
                <Typography variant="body1" marginBlock={1}>
                  {` ${log.days} days * ${log.headcount} ${
                    log.headcount > 1 ? 'contractors' : 'contractor'
                  } * $${_.round(log.blendedBillRate, 2)} * 8 `}
                </Typography>
                <Typography variant="body2">
                  {` ${log.fromDate} - ${log.toDate}`}
                </Typography>
              </TableCell>
            </TableRow>
          ),
        )}
        {project.pastRevenue ? (
          <TableRow
            sx={{
              textAlign: 'left',
              '& .MuiTableCell-root': {
                borderBottom: 'none',
              },
            }}
          >
            <TableCell>
              <Typography variant="body2" sx={{ color: COLORS.ACTION.PRIMARY }}>
                Actual Revenue
              </Typography>
              <Typography variant="h2" marginBlock={1}>
                {priceFormat({
                  value: project.pastRevenue,
                  hideCents: true,
                })}
              </Typography>
              <Typography variant="body2" marginBottom={1}>
                {` As of ${project.revenueLastInvoiceDate}`}
              </Typography>
              <Button
                onClick={redirectToInvoicesPage}
                startIcon={<ReceiptRoundedIcon />}
              >
                View invoices
              </Button>
            </TableCell>
          </TableRow>
        ) : (
          <Typography variant="body2" marginBlock={1} paddingLeft={2}>
            Actual revenue not available
          </Typography>
        )}
      </Box>
    );
  }

  if (account.projects.length > 0) {
    return (
      <Table
        sx={{
          backgroundColor: 'white',
          '& .MuiTableCell-root': { paddingBlock: '8px' },
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>Project</TableCell>
            <TableCell sx={{ textAlign: 'right' }}>Forecast</TableCell>
            <TableCell>End date</TableCell>
          </TableRow>
        </TableHead>
        <TableBody sx={{ paddingBlock: 0, marginBlock: 0, height: 0 }}>
          {account.projects.map((project: any) => (
            <TableRow
              key={project.name}
              sx={{
                height: '0 !important',
              }}
            >
              <TableCell>{project.name}</TableCell>
              <TableCell>
                <Typography
                  align={'right'}
                  sx={{
                    marginRight: project.projectedRevenue ? '' : '-20px',
                  }}
                >
                  {priceFormat({
                    value: project.projectedRevenue,
                    hideCents: true,
                  })}
                </Typography>
              </TableCell>
              <TableCell>
                {project.projectedRevenueEndDate
                  ? DateTime.fromFormat(
                      project.projectedRevenueEndDate,
                      'yyyy-LL-dd',
                    ).toFormat('LL/dd/yy')
                  : 'N/A'}
              </TableCell>
            </TableRow>
          ))}
          <TableRow
            sx={{
              height: '0 !important',
            }}
          >
            <TableCell>Total</TableCell>
            <TableCell sx={{ textAlign: 'right' }}>
              <Typography align={'right'}>
                {priceFormat({
                  value: account.projectedRevenue,
                  hideCents: true,
                })}
              </Typography>
            </TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableBody>
      </Table>
    );
  }

  return <></>;
};

interface MyPortfolioHeaderPopoverProps {
  children: JSX.Element;
  style?: CSSProperties;
  size?: 'small' | 'medium' | 'large';
  anchorEl: React.MutableRefObject<HTMLElement | null>;
  handleMouseOver: () => void;
  handleClose: () => void;
  isOpen: boolean;
}

const MyPortfolioHeaderPopover: React.FC<MyPortfolioHeaderPopoverProps> = ({
  children,
  style,
  anchorEl,
  handleMouseOver,
  handleClose,
  isOpen,
}) => {
  const id = isOpen ? 'popover-button' : undefined;

  return (
    <Box component="span" style={style}>
      <Popover
        style={{ pointerEvents: 'none' }}
        id={id}
        open={isOpen}
        anchorEl={anchorEl.current}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        PaperProps={{
          onMouseEnter: () => handleMouseOver(),
          onMouseLeave: () => handleClose(),
          sx: {
            pointerEvents: 'auto',
            padding: '0.5rem',
            gap: '1.5rem',
            maxHeight: '400px',
          },
        }}
      >
        {children}
      </Popover>
    </Box>
  );
};
